import type { IDBPDatabase } from 'idb';
import type { IAnswer, IQuestion, ITestData, ITestResponse, RefereeDB } from './types';

export class Question {
  readonly id: string;
  readonly rule: string;

  private questionTexts: Record<string, string>;
  private answerTexts: Record<string, Record<string, string>>;
  private correctAnswers: string[];
  readonly rules: string[];

  box = 1;
  private lastAsked: Date | null = null;
  private previousResults: boolean[] = [];
  numAsked = 0;
  numCorrect = 0;
  numWrong = 0;

  constructor(lang: string, question: IQuestion, answer: IAnswer, testData?: ITestData) {
    const [rule] = question.id.split('.');
    this.id = question.id;
    this.rule = rule;
    this.questionTexts = { [lang]: question.question };
    this.answerTexts = { [lang]: question.answers };
    this.correctAnswers = answer.correct;
    this.rules = answer.rule;

    if (testData) {
      this.box = testData.box;
      this.lastAsked = testData.lastAsked;
      this.previousResults = testData.previousResults;
      this.numAsked = testData.asked;
      this.numCorrect = testData.correct;
      this.numWrong = testData.wrong;
    }
  }

  get correct(): string[] {
    return this.correctAnswers;
  }

  get currentStreak(): number {
    let count = 0;
    for (const result of this.previousResults) {
      if (!result) {
        break;
      }
      count++;
    }
    return count;
  }

  get bestStreak(): number {
    let best = 0;
    let current = 0;
    for (const result of this.previousResults) {
      if (result) {
        current++;
        if (current > best) {
          best = current;
        }
      } else {
        current = 0;
      }
    }
    return best;
  }

  get isMastered(): boolean {
    return this.box === 5;
  }

  get lastAnswered(): Date | null {
    return this.lastAsked;
  }

  getQuestion(lang: string): string {
    return this.questionTexts[lang] ?? this.questionTexts['en'] ?? '';
  }

  getAnswers(lang: string): Record<string, string> {
    return this.answerTexts[lang] ?? this.answerTexts['en'] ?? {};
  }

  updateData(lang: string, question: IQuestion) {
    this.questionTexts[lang] = question.question;
    this.answerTexts[lang] = question.answers;
  }

  checkAnswer(answers: string[]): ITestResponse {
    const sorted = [...answers].sort();
    const sortedCorrect = [...this.correctAnswers].sort();

    let answeredCorrect = sorted.length === sortedCorrect.length;
    if (answeredCorrect) {
      for (let i = 0; i < sorted.length; i++) {
        if (sorted[i] !== sortedCorrect[i]) {
          answeredCorrect = false;
          break;
        }
      }
    }

    this.lastAsked = new Date();
    this.numAsked += 1;
    this.previousResults.unshift(answeredCorrect);

    if (answeredCorrect) {
      this.numCorrect += 1;
      this.box = Math.min(5, this.box + 1);
    } else {
      this.numWrong += 1;
      this.box = 1;
    }

    return {
      answeredCorrect,
      correct: this.correctAnswers,
      rules: this.rules,
    };
  }

  async persist(
    db: IDBPDatabase<RefereeDB>,
    retry?: () => Promise<IDBPDatabase<RefereeDB> | null>,
  ): Promise<void> {
    const payload = {
      box: this.box,
      asked: this.numAsked,
      correct: this.numCorrect,
      wrong: this.numWrong,
      previousResults: this.previousResults,
      lastAsked: this.lastAsked,
    };
    try {
      await db.put('questions', payload, this.id);
    } catch (err) {
      if (retry && err instanceof DOMException && err.name === 'InvalidStateError') {
        const fresh = await retry();
        if (fresh) {
          await fresh.put('questions', payload, this.id);
        }
        return;
      }
      throw err;
    }
  }

  async reset(
    db: IDBPDatabase<RefereeDB> | null,
    retry?: () => Promise<IDBPDatabase<RefereeDB> | null>,
  ): Promise<void> {
    this.box = 1;
    this.numAsked = 0;
    this.numCorrect = 0;
    this.numWrong = 0;
    this.previousResults = [];
    this.lastAsked = null;
    if (db) {
      await this.persist(db, retry);
    }
  }
}
